// Pure fulfillment helpers shared across the delivery surfaces (POS tab, board,
// driver view). No React, no data-fetching — safe to import anywhere.
// See docs/fulfillment-roadmap.md (§2.1, §2.2, §6.1).

import { DELIVERY_STATUS, DELIVERY_STATUS_LABEL } from '@/api/receipts'
import type { DeliveryRunStop } from '@/api/deliveryRuns'

// --- Type: delivery vs pickup ------------------------------------------------

// Minimal shape we read off an order/receipt search payload.
export interface FulfillmentOrderLike {
  is_delivery?: boolean
  delivery_status?: string | null
  delivery_run?: { id: number; name: string } | null
}

export type FulfillmentType = 'delivery' | 'pickup'

// Delivery vs pickup is FE-derivable today: `is_delivery` on the order, plus a
// delivery order switched to in-store pickup (`pickup_at_store`). Only delivery
// orders are run-eligible.
export function fulfillmentType(o: FulfillmentOrderLike): FulfillmentType {
  // Delivery is ONLY is_delivery === true. false / null / undefined are all
  // pickup — at delivery-disabled stores the BE omits the flag, and a strict
  // `=== false` here misclassified those pickup orders as delivery.
  if (o.is_delivery !== true) return 'pickup'
  if (o.delivery_status === DELIVERY_STATUS.PICKUP_AT_STORE) return 'pickup'
  return 'delivery'
}

// --- Stage: the order's current fulfillment state ----------------------------

export type FulfillmentStageKey =
  | 'new'
  | 'preparing'
  | 'assigned'
  | 'out_for_delivery'
  | 'delivered'
  | 'delivery_failed'
  | 'pickup_at_store'

export interface FulfillmentStage {
  key: FulfillmentStageKey
  label: string
}

// Single source for fulfillment badges across surfaces. Mostly the BE
// `delivery_status`, with one derived state: an order sitting on a run but not
// yet out for delivery reads as "Assigned". Existing DELIVERY_STATUS_LABEL
// values are reused as-is (no relabel).
export function fulfillmentStage(o: FulfillmentOrderLike): FulfillmentStage {
  switch (o.delivery_status) {
    case DELIVERY_STATUS.DELIVERED:
      return { key: 'delivered', label: DELIVERY_STATUS_LABEL.delivered }
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return { key: 'out_for_delivery', label: DELIVERY_STATUS_LABEL.out_for_delivery }
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return { key: 'delivery_failed', label: DELIVERY_STATUS_LABEL.delivery_failed }
    case DELIVERY_STATUS.PICKUP_AT_STORE:
      return { key: 'pickup_at_store', label: DELIVERY_STATUS_LABEL.pickup_at_store }
    case DELIVERY_STATUS.PREPARING:
      return o.delivery_run
        ? { key: 'assigned', label: 'Assigned' }
        : { key: 'preparing', label: DELIVERY_STATUS_LABEL.preparing }
    default:
      // 'none' or missing.
      return o.delivery_run
        ? { key: 'assigned', label: 'Assigned' }
        : { key: 'new', label: 'New' }
  }
}

// --- Run progress (no GPS) ---------------------------------------------------

// Stop statuses that are "resolved" — the driver has dealt with the stop.
const RESOLVED_STOP_STATUSES = new Set<string>([
  DELIVERY_STATUS.DELIVERED,
  DELIVERY_STATUS.DELIVERY_FAILED,
  DELIVERY_STATUS.PICKUP_AT_STORE,
])

export interface RunProgress {
  // Stops in optimized `sequence` order (nulls last, original order for ties).
  ordered: DeliveryRunStop[]
  total: number
  deliveredCount: number
  // First unresolved stop — the active target — or null when the run is done.
  current: DeliveryRunStop | null
  currentIndex: number
  // Active-leg origin: the last resolved stop before `current` (null = store).
  lastResolved: DeliveryRunStop | null
  complete: boolean
}

// Infer run progress from stop statuses alone — no GPS. The active leg runs from
// `lastResolved` (or the store, when null) to `current`. See docs §6.1.
export function runProgress(stops: DeliveryRunStop[]): RunProgress {
  const ordered = [...stops].sort((a, b) => {
    if (a.sequence == null && b.sequence == null) return 0
    if (a.sequence == null) return 1
    if (b.sequence == null) return -1
    return a.sequence - b.sequence
  })
  const total = ordered.length
  const deliveredCount = ordered.filter(
    (s) => s.delivery_status === DELIVERY_STATUS.DELIVERED,
  ).length
  const currentIndex = ordered.findIndex(
    (s) => !RESOLVED_STOP_STATUSES.has(s.delivery_status),
  )
  const current = currentIndex >= 0 ? ordered[currentIndex] : null

  let lastResolved: DeliveryRunStop | null = null
  const scanEnd = currentIndex >= 0 ? currentIndex : total
  for (let i = scanEnd - 1; i >= 0; i--) {
    if (RESOLVED_STOP_STATUSES.has(ordered[i].delivery_status)) {
      lastResolved = ordered[i]
      break
    }
  }

  return {
    ordered,
    total,
    deliveredCount,
    current,
    currentIndex,
    lastResolved,
    complete: currentIndex === -1 && total > 0,
  }
}
